import { useEffect, useRef, useState } from "react";
import { playActionSound } from "@/audio/carvingSound";
import { FLANGE } from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { useDesignerStore } from "@/state/designerStore";

/** Transient messages: export results, placement refusals, engine warnings. */
export function Notices(): React.ReactElement {
  const notices = useDesignerStore((s) => s.notices);
  const dismiss = useDesignerStore((s) => s.dismissNotice);

  useEffect(() => {
    if (notices.length === 0) return;
    const timers = notices.map((notice) =>
      window.setTimeout(() => dismiss(notice.id), notice.tone === "warn" ? 7000 : 5000),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [notices, dismiss]);

  return (
    <div className="notices" role="status" aria-live="polite">
      {notices.map((notice) => (
        <div key={notice.id} className={`notice${notice.tone === "warn" ? " notice--warn" : ""}`}>
          <span>{notice.text}</span>
          <button
            type="button"
            className="notice__close"
            onClick={() => dismiss(notice.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export function ResetDialog({ engine }: { engine: DesignerEngine }): React.ReactElement | null {
  const open = useDesignerStore((s) => s.confirmingReset);
  const setOpen = useDesignerStore((s) => s.setConfirmingReset);
  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="reset-title">
      <div className="modal">
        <h2 id="reset-title">Reset block?</h2>
        <p>
          This returns the foam to a fresh blank and removes every fixing. It can be undone with
          Ctrl+Z.
        </p>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              engine.reset();
              setOpen(false);
            }}
          >
            Reset block
          </button>
        </div>
      </div>
    </div>
  );
}

export function NewDesignDialog({ onConfirm }: { onConfirm: () => void }): React.ReactElement | null {
  const open = useDesignerStore((s) => s.confirmingNew);
  const setOpen = useDesignerStore((s) => s.setConfirmingNew);
  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="new-title">
      <div className="modal">
        <h2 id="new-title">Start a new design?</h2>
        <p>
          Your current hold is saved on this machine only, and starting fresh clears it. Export the
          STL first if you want to keep it.
        </p>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={onConfirm}>
            Start new design
          </button>
        </div>
      </div>
    </div>
  );
}

export function OverallSmoothDialog({
  engine,
}: {
  engine: DesignerEngine;
}): React.ReactElement | null {
  const open = useDesignerStore((s) => s.showOverallSmooth);
  const setOpen = useDesignerStore((s) => s.setShowOverallSmooth);
  const strength = useDesignerStore((s) => s.overallSmoothStrength);
  const setStrength = useDesignerStore((s) => s.setOverallSmoothStrength);
  const soundEnabled = useDesignerStore((s) => s.soundEnabled);
  const notify = useDesignerStore((s) => s.notify);
  const [previewing, setPreviewing] = useState(false);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState("Move the slider to preview the whole-piece smoothing.");

  useEffect(() => {
    if (!open) return;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      const result = engine.previewOverallSmooth(strength);
      setChanged(result.applied);
      setMessage(
        result.applied
          ? `Live preview at ${Math.round(strength)}%.`
          : (result.reason ?? "This setting does not change the piece."),
      );
      setPreviewing(false);
    }, 70);
    return () => window.clearTimeout(timer);
  }, [engine, open, strength]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      engine.cancelOverallSmoothPreview();
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [engine, open, setOpen]);

  if (!open) return null;

  const cancel = (): void => {
    engine.cancelOverallSmoothPreview();
    setOpen(false);
  };

  return (
    <div className="modal-backdrop modal-backdrop--preview" role="dialog" aria-modal="true" aria-labelledby="smooth-all-title">
      <div className="modal modal--smooth" aria-busy={previewing}>
        <h2 id="smooth-all-title">Smooth entire piece</h2>
        <p>Adjust the strength and inspect the model behind this panel before applying it.</p>
        <div className="smooth-preview-control">
          <div className="control__head">
            <span className="control__name">Overall smooth</span>
            <span className="control__value">{Math.round(strength)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={strength}
            onChange={(event) => setStrength(Number(event.target.value))}
            aria-label="Overall smooth preview strength"
          />
          <span className="smooth-preview-status">
            {previewing ? "Updating preview…" : message}
          </span>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={previewing || !changed}
            onClick={() => {
              if (!engine.commitOverallSmoothPreview()) return;
              if (soundEnabled) playActionSound("overall");
              notify(`Overall smooth applied at ${Math.round(strength)}%.`);
              setOpen(false);
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export function FlangePreviewDialog({
  engine,
}: {
  engine: DesignerEngine;
}): React.ReactElement | null {
  const open = useDesignerStore((s) => s.showFlangePreview);
  const setOpen = useDesignerStore((s) => s.setShowFlangePreview);
  const width = useDesignerStore((s) => s.flangeWidth);
  const setWidth = useDesignerStore((s) => s.setFlangeWidth);
  const soundEnabled = useDesignerStore((s) => s.soundEnabled);
  const notify = useDesignerStore((s) => s.notify);
  const [previewing, setPreviewing] = useState(false);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState("Move the slider to preview the flange.");
  const maxWidth = Math.min(
    FLANGE.maxWidth,
    Math.min(engine.preset.width, engine.preset.depth) * 0.4,
  );
  const effectiveWidth = Math.min(width, maxWidth);

  useEffect(() => {
    if (!open) return;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      const result = engine.previewFlange(effectiveWidth);
      setChanged(result.applied);
      setMessage(
        result.applied
          ? `Live preview at ${Math.round(effectiveWidth)} mm.`
          : (result.reason ?? "This flange does not change the piece."),
      );
      setPreviewing(false);
    }, 70);
    return () => window.clearTimeout(timer);
  }, [effectiveWidth, engine, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      engine.cancelFlangePreview();
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [engine, open, setOpen]);

  if (!open) return null;

  const cancel = (): void => {
    engine.cancelFlangePreview();
    setOpen(false);
  };

  return (
    <div className="modal-backdrop modal-backdrop--preview" role="dialog" aria-modal="true" aria-labelledby="flange-preview-title">
      <div className="modal modal--smooth" aria-busy={previewing}>
        <h2 id="flange-preview-title">Add flange</h2>
        <p>Adjust the width and inspect the smooth ramp around the model before applying it.</p>
        <div className="smooth-preview-control">
          <div className="control__head">
            <span className="control__name">Flange width</span>
            <span className="control__value">{Math.round(effectiveWidth)} mm</span>
          </div>
          <input
            type="range"
            min={FLANGE.minWidth}
            max={maxWidth}
            step={1}
            value={effectiveWidth}
            onChange={(event) => setWidth(Number(event.target.value))}
            aria-label="Flange live preview width in millimetres"
          />
          <span className="smooth-preview-status">
            {previewing ? "Updating preview…" : message}
          </span>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={cancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={previewing || !changed}
            onClick={() => {
              if (!engine.commitFlangePreview()) return;
              if (soundEnabled) playActionSound("flange");
              notify(`${Math.round(effectiveWidth)} mm flange added with a smooth full-height join.`);
              setOpen(false);
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export function ShortcutsDialog(): React.ReactElement | null {
  const open = useDesignerStore((s) => s.showShortcuts);
  const setOpen = useDesignerStore((s) => s.setShowShortcuts);
  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="keys-title">
      <div className="modal">
        <h2 id="keys-title">Shortcuts</h2>
        <dl className="shortcuts">
          <dt>Left drag on the hold</dt>
          <dd>Carve (or smooth with the Smooth tool)</dd>
          <dt>Middle-mouse drag</dt>
          <dd>Orbit the camera (Blender controls)</dd>
          <dt>
            <kbd>Shift</kbd> + middle-mouse drag
          </dt>
          <dd>Pan the camera</dd>
          <dt>Mouse wheel</dt>
          <dd>Zoom</dd>
          <dt>
            <kbd>Shift</kbd> + drag
          </dt>
          <dd>Smooth without leaving the Carve tool</dd>
          <dt>
            <kbd>[</kbd> <kbd>]</kbd>, wheel while carving
          </dt>
          <dd>Brush size (Ctrl + wheel also works)</dd>
          <dt>
            <kbd>1</kbd> <kbd>2</kbd> <kbd>3</kbd> <kbd>4</kbd> <kbd>5</kbd> <kbd>6</kbd> <kbd>7</kbd>
          </dt>
          <dd>Carve · Smooth · Fixings · Cut · Angle · Flange · Gloss paint</dd>
          <dt>
            <kbd>0</kbd> <kbd>T</kbd> <kbd>F</kbd> <kbd>B</kbd> <kbd>L</kbd> <kbd>R</kbd>
          </dt>
          <dd>3D · Top · Front · Back · Left · Right view</dd>
          <dt>
            <kbd>Enter</kbd>
          </dt>
          <dd>Make the cut or snapped angle you have drawn</dd>
          <dt>
            <kbd>Ctrl</kbd>+<kbd>Z</kbd> / <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>Z</kbd>
          </dt>
          <dd>Undo · Redo</dd>
          <dt>
            <kbd>Delete</kbd>
          </dt>
          <dd>Remove the selected fixing</dd>
          <dt>
            <kbd>Ctrl</kbd>+<kbd>E</kbd>
          </dt>
          <dd>Export STL</dd>
        </dl>
        <div className="modal__actions">
          <button type="button" className="btn btn--primary" onClick={() => setOpen(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Development diagnostics (Alt+D). Hidden in production builds unless the URL
 * carries ?dev — never part of the normal interface.
 */
export function Diagnostics({ engine }: { engine: DesignerEngine }): React.ReactElement | null {
  const visible = useDesignerStore((s) => s.showDiagnostics);
  const revision = useDesignerStore((s) => s.revision);
  const [fps, setFps] = useState(0);
  const [volume, setVolume] = useState(0);
  const frames = useRef({ count: 0, since: performance.now() });

  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    const tick = (): void => {
      frames.current.count++;
      const now = performance.now();
      if (now - frames.current.since >= 500) {
        setFps(Math.round((frames.current.count * 1000) / (now - frames.current.since)));
        frames.current = { count: 0, since: now };
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Counting a million voxels on every pointer move would distort the very
  // frame times this panel exists to report, so it is sampled on a timer.
  useEffect(() => {
    if (!visible) return;
    const measure = (): void => setVolume(engine.field.approximateVolume());
    measure();
    const timer = window.setInterval(measure, 700);
    return () => window.clearInterval(timer);
  }, [visible, engine]);

  if (!visible) return null;

  const field = engine.field;
  const bytes = field.data.byteLength + field.dirty.byteLength + engine.history.retainedBytes;
  void revision;

  return (
    <div className="panel diagnostics">
      <h3>Diagnostics</h3>
      <dl>
        <dt>FPS</dt>
        <dd>{fps}</dd>
        <dt>Display triangles</dt>
        <dd>{engine.mesher.triangleCount.toLocaleString()}</dd>
        <dt>Voxel size</dt>
        <dd>{field.h} mm</dd>
        <dt>Grid</dt>
        <dd>
          {field.nx}×{field.ny}×{field.nz}
        </dd>
        <dt>Cells</dt>
        <dd>{(field.sampleCount / 1e6).toFixed(2)} M</dd>
        <dt>Last remesh</dt>
        <dd>
          {engine.mesher.lastRebuildDurationMs.toFixed(1)} ms / {engine.mesher.lastChunkCount} chunk
        </dd>
        <dt>Block</dt>
        <dd>
          {engine.preset.width}×{engine.preset.depth}×{engine.preset.height} mm
        </dd>
        <dt>Volume (approx)</dt>
        <dd>{(volume / 1000).toFixed(0)} cm³</dd>
        <dt>Fixings</dt>
        <dd>{engine.fixings.length}</dd>
        <dt>Holes mesh</dt>
        <dd>
          {engine.solidTriangleCount > 0
            ? `${engine.solidTriangleCount.toLocaleString()} tri`
            : "—"}
        </dd>
        <dt>Undo depth</dt>
        <dd>{engine.history.depth}</dd>
        <dt>Memory</dt>
        <dd>{(bytes / (1024 * 1024)).toFixed(1)} MB</dd>
      </dl>
    </div>
  );
}

export function ExportStatusLine(): React.ReactElement | null {
  const status = useDesignerStore((s) => s.exportStatus);
  const building = useDesignerStore((s) => s.buildingHoles);

  if (building) {
    return (
      <div className="panel status">
        <span className="status__dot status__dot--busy" />
        Cutting fixing holes…
      </div>
    );
  }
  if (status.state === "idle") return null;

  return (
    <div className="panel status">
      <span
        className={`status__dot${
          status.state === "working"
            ? " status__dot--busy"
            : status.state === "error"
              ? " status__dot--error"
              : ""
        }`}
      />
      {status.state === "working" ? "Building solid…" : status.message}
    </div>
  );
}
